import _ from 'lodash-es';
import { parseParameterQuery, parameterValueLabel } from '@utils/parameters';
import { CATEGORY_TYPE } from '@utils/constants';
import { useEnumStore } from '@stores/enum';

export default function (baseTitle, options = {}) {
  const { t } = useI18n();
  const route = useRoute();
  const config = useRuntimeConfig();
  const enums = useEnumStore();
  const flatCategories = {
    [CATEGORY_TYPE.ESHOP]: useFlatCategories(CATEGORY_TYPE.ESHOP),
    [CATEGORY_TYPE.CONTENT]: useFlatCategories(CATEGORY_TYPE.CONTENT),
    [CATEGORY_TYPE.NEWS]: useFlatCategories(CATEGORY_TYPE.NEWS),
  };

  const queryValues = (value) => _.chain(_.castArray(value ?? []))
    .flatMap(item => `${item}`.split(','))
    .map(item => item.trim())
    .filter()
    .value();

  const categoryLabel = (value, types) => _.chain(types)
    .flatMap(type => flatCategories[type].items.value)
    .find({ id: +value })
    .get('name', '')
    .value();

  const parameterLabels = (regex, parameters) => _.chain(route.query)
    .transform((result, value, key) => {
      const match = key.match(regex);
      if (!match) return;

      const parameter = _.find(parameters || [], { id: +match[1] });
      if (!parameter) return;

      const parsed = parseParameterQuery(value, parameter);
      if (!parsed) return;
      const items = parsed.idValues ? parsed.idValues.map(idValue => ({ idValue })) : [parsed];
      for (const item of items) {
        const label = parameterValueLabel(item, parameter);
        if (label !== '') result.push(`${parameter.name}: ${label}`);
      }
    }, [])
    .value();

  const supplierLabel = () => {
    const routeName = route.name || '';
    if (routeName.startsWith('offers___') || routeName.startsWith('offers-slug___')) return t('text-my-offers');
    if (routeName.startsWith('requests___') || routeName.startsWith('requests-slug___')) return t('text-my-requests');
    if (routeName.startsWith('auctions___') || routeName.startsWith('auctions-slug___')) return t('text-my-auctions');
    if (routeName.startsWith('inspiration___') || routeName.startsWith('inspiration-type-slug___')) return t('text-my-inspiration');
    if (routeName.startsWith('favorites___') || routeName.startsWith('search___')) return t('text-my-favorites');
    return t('text-my-ads');
  };

  const title = computed(() => {
    const resolvedTitle = _.isFunction(baseTitle) ? baseTitle() : unref(baseTitle);
    const parts = [];
    const fulltext = queryValues(route.query.fulltext).join(' | ');
    const category = route.query.idCategory
      ? categoryLabel(route.query.idCategory, [CATEGORY_TYPE.ESHOP])
      : categoryLabel(route.query.idPostCategory, [CATEGORY_TYPE.CONTENT, CATEGORY_TYPE.NEWS]);
    const location = queryValues(route.query.location).join(' | ');

    if (route.query.idSupplier) parts.push(supplierLabel());
    if (category) parts.push(category);
    if (location) parts.push(location);

    _.forEach(['maxKmFromHome', 'maxKmFromLocation'], key => {
      _.forEach(queryValues(route.query[key]), value => {
        const distance = +value;
        if (distance) parts.push(`${distance} ${t('text-unit-km')}`);
      });
    });

    parts.push(...parameterLabels(/^p\[(\d+)\]$/, enums.eshopParameters));
    parts.push(...parameterLabels(/^t\[(\d+)\]$/, enums.contentParameters));

    const prefix = options.includeFulltext && fulltext ? `${resolvedTitle}: ${fulltext}` : resolvedTitle;
    return [prefix, ...parts].filter(Boolean).join(' | ');
  });

  const ogUrl = computed(() => {
    const baseUrl = config.public.appBaseUrl.replace(/\/$/, '');
    return `${baseUrl}${route.fullPath.split('#')[0]}`;
  });

  return { title, ogUrl };
}
